"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { type HeadingNode } from "@ui/components/core/TableOfContents/SimpleTableOfContents";
import { clsx } from "clsx";
import { type MotionValue, useAnimationFrame, useMotionValue, useTransform } from "motion/react";
import * as m from "motion/react-m";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { BaseLink } from "@ui/components/core/Link";

interface ArticleScrollSpyTOCProps {
  className?: string;
  style?: React.CSSProperties;
  headings: HeadingNode[];
  heroId?: string;
  /** When false, only pips aligned with heading rows render; gaps keep the same vertical rhythm. Default true. */
  showInBetweenPips?: boolean;
}

/** ~63% toward target in one τ; attack = fast rise, release = slow decay (VU-style ballistics). */
const SECTION_RATIO_ATTACK_TAU_SEC = 0.001;
const SECTION_RATIO_RELEASE_TAU_SEC = 0.1;

type SectionBounds = { start: number; end: number } | null;

function elementDocumentTop(el: Element): number {
  const r = el.getBoundingClientRect();
  return r.top + window.scrollY;
}

function elementDocumentBottom(el: Element): number {
  const r = el.getBoundingClientRect();
  return r.bottom + window.scrollY;
}

/** Section end when there is no next heading top: bottom of the heading element’s parent (falls back if invalid). */
function sectionEndYFromHeadingParent(
  headingId: null | string | undefined,
  sectionStartY: number,
  fallbackY: number,
): number {
  if (!headingId) return fallbackY;
  const el = document.getElementById(headingId);
  const parent = el?.parentElement;
  if (!el || !parent) return fallbackY;
  const bottom = elementDocumentBottom(parent);
  if (bottom <= sectionStartY) return fallbackY;
  return bottom;
}

function resolveHeadingId(heading: HeadingNode, index: number, heroId: string | undefined): string | undefined {
  return heading.id || (index === 0 && heroId ? heroId : undefined);
}

function measureSectionBounds(headings: HeadingNode[], heroId: string | undefined): SectionBounds[] {
  if (headings.length === 0) return [];

  const docBottom = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight ?? 0);

  const headingIds = headings.map((heading, index) => resolveHeadingId(heading, index, heroId));
  const sectionStarts = headingIds.map(id => {
    if (!id) return null;
    const el = document.getElementById(id);
    if (!el) return null;
    return elementDocumentTop(el);
  });

  return headings.map((_, index) => {
    const start = sectionStarts[index];
    if (start === null) return null;

    const idCurrent = headingIds[index];
    const nextStart = sectionStarts[index + 1];
    const end =
      nextStart !== null && nextStart !== undefined
        ? nextStart
        : sectionEndYFromHeadingParent(idCurrent, start, docBottom);

    if (end <= start) return null;
    return { start, end };
  });
}

/**
 * For each heading, a 0–1 “how prominent is this section in view?” score.
 *
 * Hybrid of (a) share of the viewport the section occupies and (b) share of the section that is visible.
 * Using the max of the two fixes short sections: when the whole section fits in the viewport, (b) is 1
 * so the anchor pip can go full scale even though (a) may be small. Long sections that fill the screen
 * still hit 1 via (a).
 *
 * The last section (and any section whose following heading has no DOM node) ends at the **bottom** of
 * the current heading’s **parent** element, not the document scroll height.
 */
function computeSectionViewportRatios(
  sectionBounds: SectionBounds[],
  sectionCount: number,
  viewportTop: number,
  viewportHeight: number,
): number[] {
  const viewportBottom = viewportTop + viewportHeight;
  if (viewportHeight <= 0) {
    return Array.from({ length: sectionCount }, () => 0);
  }

  return Array.from({ length: sectionCount }, (_, i) => {
    const bounds = sectionBounds[i];
    if (!bounds) return 0;

    const sectionHeight = bounds.end - bounds.start;
    if (sectionHeight <= 0) return 0;

    const overlapTop = Math.max(bounds.start, viewportTop);
    const overlapBottom = Math.min(bounds.end, viewportBottom);
    const overlap = Math.max(0, overlapBottom - overlapTop);

    const fracOfViewport = overlap / viewportHeight;
    const fracOfSection = overlap / sectionHeight;
    return Math.min(1, Math.max(fracOfViewport, fracOfSection));
  });
}

/** Matches `tw-h-0.25` (1px) + `tw-mt-[0.3125rem]` and `tw-top-[0.53125rem]` on the pip track. */
function getPipLayoutMetrics() {
  const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const pipHeightPx = 1;
  const gapPx = 0.3125 * rootFontPx;
  const step = pipHeightPx + gapPx;
  const topOffsetPx = 0.53125 * rootFontPx;
  return { pipHeightPx, rootFontPx, step, topOffsetPx };
}

/**
 * `fillHeight` = distance from pip track top to target (last heading first-line center).
 * Last pip center sits at `(n - 1) * step + pipHeight / 2`.
 */
function countPipsToTargetLineCenter(fillHeightPx: number, pipHeightPx: number, step: number): number {
  if (fillHeightPx <= pipHeightPx / 2) return 1;
  return Math.max(1, Math.ceil((fillHeightPx - pipHeightPx / 2) / step) + 1);
}

/** Y positions (px) of each heading’s first-line center, relative to the pip track top (below `tw-top-[0.53125rem]`). */
function measureHeadingAnchorsPx(container: HTMLElement, list: HTMLUListElement | null): number[] {
  const { topOffsetPx } = getPipLayoutMetrics();
  const innerTopViewport = container.getBoundingClientRect().top + topOffsetPx;
  if (!list) return [];

  const anchors: number[] = [];
  for (const li of list.querySelectorAll(":scope > li")) {
    const span = li.querySelector<HTMLElement>("span[data-toc-line-target]");
    const measureEl = span ?? (li instanceof HTMLElement ? li : null);
    if (!measureEl) continue;
    const rects = measureEl.getClientRects();
    if (rects.length === 0) continue;
    const r = rects[0]!;
    const cy = r.top + r.height / 2;
    anchors.push(cy - innerTopViewport);
  }
  return anchors;
}

function measurePipLayout(
  container: HTMLElement,
  list: HTMLUListElement | null,
): {
  anchorsPx: number[];
  anchorPipIndices: Set<number>;
  pipCount: number;
  pipHeightPx: number;
  step: number;
} {
  const { pipHeightPx, rootFontPx, step, topOffsetPx } = getPipLayoutMetrics();
  const anchorsPx = measureHeadingAnchorsPx(container, list);
  const containerRect = container.getBoundingClientRect();
  const innerTopViewport = containerRect.top + topOffsetPx;

  const lastLi = list?.lastElementChild;
  if (lastLi instanceof HTMLElement) {
    const inlineText = lastLi.querySelector<HTMLElement>(":scope > span[data-toc-line-target]");
    const measureEl = inlineText ?? lastLi;
    const rects = measureEl.getClientRects();
    if (rects.length > 0) {
      const firstLine = rects[0]!;
      const firstLineCenterY = firstLine.top + firstLine.height / 2;
      const fillHeight = firstLineCenterY - innerTopViewport;
      if (Number.isFinite(fillHeight)) {
        const pipCount = countPipsToTargetLineCenter(fillHeight, pipHeightPx, step);
        return {
          anchorsPx,
          anchorPipIndices: getAnchorPipIndices(anchorsPx, step, pipHeightPx, pipCount),
          pipCount,
          pipHeightPx,
          step,
        };
      }
    }
  }

  const halfLinePx = (1.125 * rootFontPx) / 2;
  const fillHeight = Math.max(pipHeightPx, containerRect.height - topOffsetPx - halfLinePx);
  const pipCount = countPipsToTargetLineCenter(fillHeight, pipHeightPx, step);
  return {
    anchorsPx,
    anchorPipIndices: getAnchorPipIndices(anchorsPx, step, pipHeightPx, pipCount),
    pipCount,
    pipHeightPx,
    step,
  };
}

/** mv value 0 → scale 1, mv value 1 → scale 2 */
function sectionScaleX(mv: number): number {
  const t = Math.max(0, Math.min(1, mv));
  return 1 + 1 * t;
}

/** Higher = snappier dip toward the trough (1) between anchors. */
const TROUGH_CURVE_POWER = 3;

/**
 * Between two anchor scales, dip to 1 at the midpoint (u = 0.5).
 * Uses a power curve so scale falls toward 1 quickly off each anchor, then eases into the next.
 */
function troughInterpolate(scale0: number, scale1: number, u: number): number {
  const p = TROUGH_CURVE_POWER;
  if (u <= 0.5) {
    const k = u / 0.5;
    return 1 + (scale0 - 1) * (1 - k) ** p;
  }
  const k = (u - 0.5) / 0.5;
  return 1 + (scale1 - 1) * k ** p;
}

function pipCenterYpx(pipIndex: number, step: number, pipHeightPx: number): number {
  return pipIndex * step + pipHeightPx / 2;
}

function getAnchorPipIndices(anchorsPx: number[], step: number, pipHeightPx: number, pipCount: number): Set<number> {
  const half = pipHeightPx / 2;
  const indices = new Set<number>();
  for (const anchor of anchorsPx) {
    if (!Number.isFinite(anchor)) continue;
    const nearestIndex = Math.round((anchor - half) / step);
    if (nearestIndex >= 0 && nearestIndex < pipCount) {
      indices.add(nearestIndex);
    }
  }
  return indices;
}

/** Horizontal scale (1–2) along the pip track at vertical position `trackCenterY` (px from pip rail top). */
function scaleXAtPipTrackY(
  trackCenterY: number,
  mv: number[],
  anchorsPx: number[],
  step: number,
  pipHeightPx: number,
  pipCount: number,
): number {
  if (anchorsPx.length === 0 || mv.length === 0) return 1;

  const t = trackCenterY;
  const trackEnd = pipCenterYpx(pipCount - 1, step, pipHeightPx);
  const n = Math.min(anchorsPx.length, mv.length);
  const a = anchorsPx.slice(0, n);
  const scales = mv.slice(0, n).map(sectionScaleX);

  const a0 = a[0]!;
  const lastA = a[n - 1]!;

  if (t <= a0) {
    if (a0 <= 0) return scales[0]!;
    return 1 + (scales[0]! - 1) * (t / a0);
  }

  if (t >= lastA) {
    if (trackEnd <= lastA) return scales[n - 1]!;
    return scales[n - 1]! + (1 - scales[n - 1]!) * ((t - lastA) / (trackEnd - lastA));
  }

  let i = 0;
  while (i < n - 1 && t > a[i + 1]!) {
    i++;
  }
  if (i >= n - 1) return scales[n - 1]!;

  const start = a[i]!;
  const end = a[i + 1]!;
  if (end <= start) return scales[i]!;
  const u = (t - start) / (end - start);
  return troughInterpolate(scales[i]!, scales[i + 1]!, u);
}

function scaleXForPip(
  pipIndex: number,
  mv: number[],
  anchorsPx: number[],
  step: number,
  pipHeightPx: number,
  pipCount: number,
): number {
  return scaleXAtPipTrackY(pipCenterYpx(pipIndex, step, pipHeightPx), mv, anchorsPx, step, pipHeightPx, pipCount);
}

/** Map section scaleX (1–2) to opacity; same curve shape as Pip, different output range. */
function opacityFromScaleX(scaleXValue: number, minOpacity: number, maxOpacity: number): number {
  const t = Math.max(0, Math.min(1, scaleXValue - 1));
  return minOpacity + t * (maxOpacity - minOpacity);
}

/** Same t as opacityFromScaleX(s, 0.5, 1): inactive #777169 (gray-500) → active #000. */
function tocHeadingTextColorFromScaleX(scaleXValue: number): string {
  const t = Math.max(0, Math.min(1, scaleXValue - 1));
  const r = Math.round(119 * (1 - t));
  const g = Math.round(113 * (1 - t));
  const b = Math.round(105 * (1 - t));
  return `rgb(${r} ${g} ${b})`;
}

export const ArticleScrollSpyTOC = ({
  headings,
  className,
  style,
  heroId,
  showInBetweenPips = true,
}: ArticleScrollSpyTOCProps) => {
  const mv = useMotionValue<number[]>(headings.map(() => 0));
  const headingsRef = useRef(headings);
  const heroIdRef = useRef(heroId);
  const hasInitializedRef = useRef(false);
  headingsRef.current = headings;
  heroIdRef.current = heroId;

  const targetRatiosRef = useRef<number[]>(headings.map(() => 0));
  const smoothedRatiosRef = useRef<number[]>(headings.map(() => 0));
  const sectionBoundsRef = useRef<SectionBounds[]>([]);
  const scrollRafRef = useRef<number | null>(null);
  const resizeRafRef = useRef<number | null>(null);

  const updateTargets = useCallback(
    (immediate = false) => {
      const next = computeSectionViewportRatios(
        sectionBoundsRef.current,
        headingsRef.current.length,
        window.scrollY,
        window.innerHeight,
      );
      targetRatiosRef.current = next;
      const len = next.length;
      const s = smoothedRatiosRef.current;
      const shouldSetImmediately = immediate || !hasInitializedRef.current;
      if (shouldSetImmediately) {
        hasInitializedRef.current = true;
        const immediateValues = next.slice();
        smoothedRatiosRef.current = immediateValues;
        mv.set(immediateValues);
        return;
      }
      if (s.length !== len) {
        smoothedRatiosRef.current = Array.from({ length: len }, (_, i) => s[i] ?? next[i] ?? 0);
      }
    },
    [mv],
  );

  const measureSections = useCallback(() => {
    sectionBoundsRef.current = measureSectionBounds(headingsRef.current, heroIdRef.current);
  }, []);

  useLayoutEffect(() => {
    const scheduleSectionMeasure = () => {
      if (resizeRafRef.current !== null) return;
      resizeRafRef.current = window.requestAnimationFrame(() => {
        resizeRafRef.current = null;
        measureSections();
        updateTargets(true);
      });
    };

    const scheduleScrollUpdate = () => {
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        updateTargets();
      });
    };

    measureSections();
    updateTargets(true);

    window.addEventListener("scroll", scheduleScrollUpdate, { passive: true });
    window.addEventListener("resize", scheduleSectionMeasure, { passive: true });

    const ro = new ResizeObserver(scheduleSectionMeasure);
    ro.observe(document.documentElement);
    if (document.body) {
      ro.observe(document.body);
    }

    return () => {
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      if (resizeRafRef.current !== null) {
        window.cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      window.removeEventListener("scroll", scheduleScrollUpdate);
      window.removeEventListener("resize", scheduleSectionMeasure);
      ro.disconnect();
    };
  }, [headings.length, measureSections, updateTargets]);

  useLayoutEffect(() => {
    measureSections();
    updateTargets(true);
  }, [headings, heroId, measureSections, updateTargets]);

  useAnimationFrame((_time, delta) => {
    const dtSec = Math.min(0.12, delta / 1000);

    const target = targetRatiosRef.current;
    let smoothed = smoothedRatiosRef.current;
    if (target.length !== smoothed.length) {
      smoothed = Array.from({ length: target.length }, (_, i) => smoothed[i] ?? target[i] ?? 0);
      smoothedRatiosRef.current = smoothed;
    }

    const len = target.length;
    if (len === 0) return;

    let changed = false;
    for (let i = 0; i < len; i++) {
      const tg = target[i]!;
      const c = smoothed[i]!;
      const tau = tg >= c ? SECTION_RATIO_ATTACK_TAU_SEC : SECTION_RATIO_RELEASE_TAU_SEC;
      const alpha = 1 - Math.exp(-dtSec / tau);
      const n = c + (tg - c) * alpha;
      smoothed[i] = n;
      if (Math.abs(n - c) > 1e-5) changed = true;
    }

    const curMv = mv.get();
    if (changed || curMv.length !== len) {
      mv.set(smoothed.slice());
    }
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [pipLayout, setPipLayout] = useState<{
    anchorsPx: number[];
    anchorPipIndices: Set<number>;
    pipCount: number;
    pipHeightPx: number;
    step: number;
  }>({
    anchorsPx: [],
    anchorPipIndices: new Set(),
    pipCount: 0,
    pipHeightPx: 1,
    step: 6,
  });
  const [layoutVersion, setLayoutVersion] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const layoutRoot: HTMLElement = container;

    function measure() {
      setPipLayout(measurePipLayout(layoutRoot, listRef.current));
      setLayoutVersion(v => v + 1);
    }

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [headings]);

  const { anchorsPx, anchorPipIndices, pipCount, pipHeightPx, step } = pipLayout;

  return (
    <div ref={containerRef} className={clsx("tw-relative tw-pl-10", className)} style={style}>
      {pipCount === 0 && (
        // Placeholder
        <div
          className='tw-absolute tw-top-[0.53125rem] tw-bottom-0 tw-left-0 tw-w-3'
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgb(0 0 0 / 0.1) 0px, rgb(0 0 0 / 0.1) 0.0625rem, transparent 0.0625rem, transparent 0.375rem)",
          }}
        />
      )}

      <div className='tw-absolute tw-top-[0.53125rem] tw-left-0'>
        {Array.from({ length: pipCount }).map((_, index) => {
          const isAnchorPip = anchorPipIndices.has(index);
          const spacerClass = clsx("tw-w-3 tw-h-0.25 tw-shrink-0", index > 0 && "tw-mt-[0.3125rem]");

          if (!showInBetweenPips && !isAnchorPip) {
            return <div key={index} aria-hidden className={spacerClass} />;
          }

          return (
            <Pip
              key={index}
              anchorsPx={anchorsPx}
              className={clsx(index > 0 && "tw-mt-[0.3125rem]")}
              mv={mv}
              pipCount={pipCount}
              pipHeightPx={pipHeightPx}
              pipIndex={index}
              step={step}
            />
          );
        })}
      </div>
      <ul ref={listRef} role='list' className='tw-text-xs/[1.125rem]'>
        {headings.map((heading, headingIndex) => (
          <TocHeadingRow
            key={`${headingIndex}-${heading.id ?? ""}-${layoutVersion}`}
            anchorsPx={anchorsPx}
            heading={heading}
            headingIndex={headingIndex}
            isFirst={headingIndex === 0}
            isLast={headingIndex === headings.length - 1}
            mv={mv}
            pipCount={pipCount}
            pipHeightPx={pipHeightPx}
            step={step}
          />
        ))}
      </ul>
    </div>
  );
};

function Pip({
  anchorsPx,
  className,
  mv,
  pipCount,
  pipHeightPx,
  pipIndex,
  step,
}: {
  anchorsPx: number[];
  className?: string;
  mv: MotionValue<number[]>;
  pipCount: number;
  pipHeightPx: number;
  pipIndex: number;
  step: number;
}) {
  const scaleX = useTransform(mv, arr => scaleXForPip(pipIndex, arr, anchorsPx, step, pipHeightPx, pipCount));

  const opacity = useTransform(scaleX, s => opacityFromScaleX(s, 0.1, 0.5));

  return (
    <m.div className={clsx("tw-origin-left tw-w-3 tw-h-0.25 tw-bg-black", className)} style={{ opacity, scaleX }} />
  );
}

function TocHeadingRow({
  anchorsPx,
  heading,
  mv,
  pipCount,
  pipHeightPx,
  step,
  isFirst,
  isLast,
  headingIndex,
}: {
  anchorsPx: number[];
  heading: HeadingNode;
  isFirst: boolean;
  isLast: boolean;
  headingIndex: number;
  mv: MotionValue<number[]>;
  pipCount: number;
  pipHeightPx: number;
  step: number;
}) {
  const scaleX = useTransform(mv, arr => {
    const y = anchorsPx[headingIndex];
    if (y === undefined || !Number.isFinite(y)) return 1;
    return scaleXAtPipTrackY(y, arr, anchorsPx, step, pipHeightPx, pipCount);
  });

  const color = useTransform(scaleX, tocHeadingTextColorFromScaleX);

  return (
    <li className={clsx("tw-relative", !isFirst && "tw-mt-4.5")}>
      {isLast && <div className='tw-absolute tw-top-3 tw-bottom-0 -tw-left-10 tw-w-3 tw-bg-paper' />}
      <m.span className='tw-inline' data-toc-line-target style={{ color }}>
        <BaseLink
          href={`#${heading.id}`}
          className='tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm hover:tw-text-black tw-transition-colors'
          onClick={() => {
            captureEvent(EventName.mw_table_of_contents_click, { heading: heading.text });
          }}
        >
          {heading.text}
        </BaseLink>
      </m.span>
    </li>
  );
}
